/**
 * CREDIT NOTES — the ONE decision point for their vocabulary and their rules.
 *
 * The same standing as `src/lib/targets.ts`, `src/lib/company-projects.ts` and
 * `src/lib/quote-utils.ts`: every surface that renders a credit note, a credit
 * balance or a destructive control over one comes through here, so no two of
 * them can spell one fact differently or word one consequence two ways.
 *
 * ⛔ **NOTHING HERE FETCHES OR WRITES. IT DECIDES.** There are no hooks, no
 * `api` imports and no request builders, and none may be added. The pages own
 * their own requests and their own draft state; this module answers *"given
 * what the server said about this note, what may the screen say and what may
 * it offer?"* — which is the half that must not be reimplemented per surface.
 *
 * ## ⛔ THE NO-HELPER-TEXT PROHIBITION, AND WHAT THIS FILE IS
 *
 * This app forbids helper text outright: no `hint` on a field, no
 * `description` on a `Modal` or a `Panel`, no explanatory sentence under a
 * control. **Everything sentence-shaped in this file is one of the four
 * documented EXCEPTIONS**, and each one says at its own definition which
 * exception it is claiming, so a future helper-text sweep can tell them apart
 * rather than deleting a warning about money.
 *
 * ```
 * EXCEPTION 1  a REFUSAL — the server said no, or will, and why
 * EXCEPTION 2  WHY A CONTROL IS DISABLED — a bare `disabled` makes a
 *              permission denial, a derived value and an ordinary state
 *              identical
 * EXCEPTION 3  a DESTRUCTIVE CONSEQUENCE — `ConfirmDialog` body text naming
 *              what is about to be destroyed
 * EXCEPTION 4  a FACT ABOUT THE DATA the reader cannot otherwise see —
 *              the same standing as a `Combobox`'s truncation footnote
 * ```
 *
 * ⚠️ **A LABEL IS NOT HELPER TEXT AT ALL and needs no exception.** The
 * prohibition is about prose attached to a CONTROL. A column heading, a field
 * label and an `EmptyState` description are structural — the house rule
 * *requires* an empty state to say what to do next — so the wording constants
 * below are in scope of this module's one-copy rule and out of scope of the
 * sweep. They are still tagged, because a sweep reading only the string cannot
 * tell a heading from a hint.
 *
 * ## 🔴 THE THREE RULES THAT GOVERN EVERY SENTENCE BELOW
 *
 * **1. `isApproved` and `isVoid` ARE NOT COMPLEMENTS** — and the reason is the
 * THIRD state, not an overlap.
 *
 * ⚠️ *SUPERSEDED, AND IT WAS WRONG: "A note is approved and THEN voided, so
 * both flags are `true` on that row."* **Both are never true at once.**
 * Migration 197 seeds Void as `is_approved 0, is_void 1`, and its own guard
 * aborts the deploy on any row carrying both.
 *
 * What the rule forbids is reading either flag as the other's NEGATION, because
 * **a DRAFT is neither**. `NOT isVoid` does not mean approved — it counts every
 * draft nobody has approved, which is the balance defect migration 197 names.
 * `NOT isApproved` does not mean void.
 *
 * 🔴 **READ `spendable`.** The server derives it as `isApproved AND NOT isVoid`
 * precisely so nothing here has to get this right, and nothing in this file
 * tests either flag on its own.
 *
 * ⚠️ *SUPERSEDED, the clause that survived the first correction: "The
 * correction does not soften rule 1: a sentence built on `isApproved` alone
 * still describes money that may have been withdrawn as live — it is simply
 * wrong about a DIFFERENT row than this paragraph used to claim."* **It is
 * `NOT isVoid` that is the unsafe read, and `isApproved` that is the safe
 * one.** Migration 197 says which is which in terms: *"Read is_approved for
 * the balance. Read is_void only to explain a terminal row on screen."* The
 * Void row carries `is_approved 0`, so `isApproved` alone cannot report
 * withdrawn money as live.
 *
 * ⚠️ **Rule 1 stands and is worth stating precisely, because the hazard it
 * guards is the DRAFT and not the voided row.** Naming the wrong half sends
 * the next reader to defend against the harmless one — which is what both
 * halves of this correction had done.
 *
 * **2. `remainingAmount` IS REPORTED EVEN ON A DRAFT.** A draft carries its
 * whole value there and can be spent on nothing whatever. So
 * `remainingAmount` alone never means *credit available* — it has to be read
 * beside {@link spendableOf}, and folding the two makes a draft
 * indistinguishable from a fully-spent note. That is why
 * {@link CREDIT_REMAINING_LABEL} and {@link CREDIT_AVAILABLE_LABEL} are two
 * constants rather than one.
 *
 * **3. MONEY IS A DECIMAL STRING.** Never `Number()`, never `parseFloat`,
 * never `?? 0`. Everything here renders through `formatDecimalString`, which
 * groups digits as text without ever touching a float; anything doing
 * arithmetic reads `src/lib/decimal.ts`. **This file does no arithmetic at
 * all**, which is deliberate — see {@link balanceLabel}.
 *
 * ## ⚠️ AND ONE RULE ABOUT THE SENTENCES THEMSELVES
 *
 * **Never assert the absence of something.** *"There is no way to…"*, *"this
 * cannot be…"*, *"that is not built yet"* — every one is a claim about the
 * whole system made from one file, it rots the moment somebody builds the
 * thing, and the builder is never the one re-reading the sentence. **Name the
 * control that DOES exist** instead; a positive claim fails visibly.
 */

import type { BadgeTone } from '@/components/ui';
import { formatDecimalString } from './format';
import type {
  AllocationPush,
  CompanyCreditBalance,
  CreditNote,
  CreditNoteAllocation,
  CreditNotePushReport,
  Invoice,
  InvoiceStatus,
} from './types';

/* -------------------------------------------------------------------------
 * The wording — one copy each, so no two surfaces spell one fact differently.
 * --------------------------------------------------------------------- */

/**
 * The module's own name, wherever it is named — a nav row, a page header, a
 * panel on a company record, a confirmation dialog's title.
 *
 * **Not helper text** — a label. Tagged so a sweep can see that at a glance.
 *
 * ⚠️ **Plural, and it names the RECORDS rather than the balance they produce.**
 * A credit note is a document somebody raises; {@link CREDIT_BALANCE_LABEL} is
 * what a company is left holding once those documents are approved and partly
 * spent. Two different things, and a heading using one word for both is how a
 * reader comes to believe a balance is a single record they can open.
 */
export const CREDIT_NOTES_LABEL = 'Credit notes';

/**
 * The heading over what a company currently holds — the aggregate, not a
 * document.
 *
 * **Not helper text** — a label.
 *
 * ⚠️ **It heads {@link balanceLabel}'s output, which is PER CURRENCY**, so the
 * heading is deliberately unitless. A heading reading "Credit balance (GBP)"
 * would be a claim this module refuses to let a cell make — see
 * {@link balanceLabel} for why there is no single figure to put a code on.
 */
export const CREDIT_BALANCE_LABEL = 'Credit balance';

/**
 * The wording for money that is **approved, not voided, and not yet spent** —
 * credit somebody can actually put against an invoice.
 *
 * **Not helper text** — a label. ⛔ **But it carries a rule, and it is rule 2
 * in the header.**
 *
 * ⛔ **THIS LABEL MAY ONLY SIT OVER A FIGURE THAT PASSED {@link spendableOf}.**
 * `remainingAmount` is reported on a DRAFT too — a draft holds its whole value
 * there and can be spent on nothing — so wording an unchecked
 * `remainingAmount` as *credit available* states that money is spendable when
 * the server says it is not. Use {@link CREDIT_REMAINING_LABEL} for the raw
 * figure; this one is a claim about what the figure can DO.
 *
 * ⚠️ **The two are one word apart and swapping them COMPILES**, prints a
 * plausible amount, and is wrong in the reassuring direction — a draft
 * presented as money to spend. Nothing typechecks which label sits over which
 * figure; this paragraph is the whole guard.
 */
export const CREDIT_AVAILABLE_LABEL = 'Credit available';

/**
 * The note's own unspent figure, worded as the neutral fact it is.
 *
 * **Not helper text** — a label.
 *
 * ⚠️ **Safe on every note in every state, which is exactly why it exists.** On
 * a draft it reads as *"this note's value, none of it spent"* — true — where
 * {@link CREDIT_AVAILABLE_LABEL} over the same figure would be false. On a
 * voided note it is true and useless, which is a legible combination; the void
 * state is carried by the row's own status, not by relabelling its money.
 */
export const CREDIT_REMAINING_LABEL = 'Remaining';

/**
 * How much of a note has been put against invoices.
 *
 * **Not helper text** — a label.
 *
 * ⚠️ **"Allocated", never "used" or "spent".** The word has to match the thing
 * that is undone: un-applying credit DELETES an allocation row (see
 * {@link voidWarning}), so a reader who allocated is a reader who can be told
 * precisely what disappears. "Spent" implies a transaction with a counterpart,
 * which is the reading {@link voidWarning} exists to prevent.
 */
export const CREDIT_ALLOCATED_LABEL = 'Allocated';

/**
 * The company panel's empty state — no credit notes on this account at all.
 *
 * **EXCEPTION 4** (a fact about the data) plus the structural `EmptyState`
 * rule, which requires an empty state to say what to do next.
 *
 * ⚠️ **It names the control that DOES exist rather than asserting an
 * absence.** *"No credit has been raised for this company"* on its own is a
 * dead end; naming the button is what makes the state actionable, and it is
 * the form of sentence that fails visibly if the control is ever renamed
 * rather than rotting silently.
 *
 * ⛔ **It must not imply credit is on its way.** A company with no credit notes
 * is the ordinary state of almost every company, not a gap waiting to be
 * filled.
 */
export const NO_CREDIT_NOTES_MESSAGE =
  'No credit notes have been raised for this company. Use New credit note to raise one.';

/**
 * The balance panel's empty state — notes may exist, but none of them is
 * spendable today.
 *
 * **EXCEPTION 4** — and it is a fact the reader genuinely cannot otherwise see.
 *
 * 🔴 **THIS IS NOT THE SAME STATE AS {@link NO_CREDIT_NOTES_MESSAGE} AND THE
 * TWO MUST NEVER SHARE A SENTENCE.** A company with three draft notes and a
 * company with no notes at all both show an empty balance, and they need
 * opposite next actions: one is *approve them*, the other is *raise one*. A
 * single "no credit" sentence over both sends somebody hunting for a note that
 * does not exist, or to raise a second one beside three that are waiting.
 *
 * ⚠️ *SUPERSEDED: "Rule 1 is what produces this state most often: an approved
 * note that was later voided is still an approved note, and it is not
 * spendable."* **A voided note is NOT an approved note** — its status row
 * carries `is_approved 0` (migration 197), so the two never overlap on one
 * row.
 *
 * ⚠️ **The state is reached from BOTH ENDS, which is exactly why this sentence
 * says *spendable* rather than *approved***: a note nobody has approved yet,
 * and a note that was approved and has since been voided. Neither counts, and
 * only `spendable` covers both without naming a flag.
 */
export const NO_CREDIT_BALANCE_MESSAGE =
  'This company holds no spendable credit. A credit note counts here once it is approved and while it has not been voided.';

/* -------------------------------------------------------------------------
 * Whether a note's money is live.
 * --------------------------------------------------------------------- */

/**
 * Whether this note's remaining value may be put against an invoice —
 * **read from the server, never re-derived.**
 *
 * ⛔ **IT MUST NOT RECOMPUTE `isApproved && !isVoid`, AND THAT IS THE WHOLE
 * POINT OF THE FUNCTION.** The server derives `spendable` and serves it on the
 * row; a client-side re-derivation is **a second answer to a question that
 * already has one**, and the two only ever diverge on the row nobody set up on
 * purpose — which is the proxy-field shape this repo has shipped four times.
 * Even today, with the two flags apparently sufficient, the server's rule may
 * legitimately consult things this app cannot see: whether the note's currency
 * is still active, whether its company is linked, whether an accountant has
 * reversed it upstream. **Read the field that was written to answer this
 * question.**
 *
 * ⚠️ *SUPERSEDED, AND IT NAMED THE WRONG HALF: "AND THE OBVIOUS RE-DERIVATION
 * IS WRONG EVEN ON THE TWO FLAGS. `isApproved` and `isVoid` are not
 * complements (rule 1): a voided note carries both, so `isApproved` alone
 * reports withdrawn money as live."* **A voided note carries `is_approved 0`.**
 * Migration 197 seeds it that way and its own guard aborts the deploy on any
 * row carrying both, so `isApproved` alone is the SAFE read — and that
 * migration says to use it: *"Read is_approved for the balance."*
 *
 * ⚠️ **THE LOCAL TEST THAT IS ACTUALLY WRONG IS `!isVoid`**, which counts every
 * DRAFT nobody has approved as spendable credit. That is the balance defect
 * rule 1 exists to name, and it is the "simpler" test somebody reaches for.
 * **The refusal to re-derive is unchanged and does not rest on this** — the
 * paragraph above is the reason, and it holds whichever flag is misread.
 *
 * ⚠️ **It is a FUNCTION rather than a direct property read at the call sites,
 * so there is one place to change if the wire ever renames the field** — and
 * one place for this comment to live, which is the half that actually stops
 * the re-derivation coming back.
 */
export function spendableOf(note: CreditNote): boolean {
  return note.spendable;
}

/**
 * The badge tone for a note's status, **decided by the FLAGS, never the name.**
 *
 * The status NAME is admin-editable and is rendered as the badge's text; the
 * tone is a hint beside it, so colour is never the only signal.
 *
 * ⚠️ **`isVoid` is tested before `isApproved`.** Against the seeded master the
 * two are never both true, so the order changes nothing today — but it is the
 * order that fails safe if a row ever carries both: a withdrawn note must never
 * be painted green.
 *
 * ⚠️ **`CreditNotesPage` carries a module-local copy of this (`statusTone`)**,
 * written first and identical in behaviour. It is not imported there yet;
 * folding that copy onto this one is the follow-up, so the list and the
 * company panel cannot come to disagree about one note's colour.
 */
export function creditNoteStatusTone(status: CreditNote['status']): BadgeTone {
  if (status.isVoid) return 'neutral';
  if (status.isApproved) return 'success';
  if (status.isAwaitingApproval) return 'warning';
  return 'info';
}

/* -------------------------------------------------------------------------
 * Whether the edit controls may be offered.
 * --------------------------------------------------------------------- */

/**
 * Why the edit controls are withheld, or that they are not.
 *
 * `reason` is **EXCEPTION 2** — a why-this-is-disabled sentence. A bare
 * `disabled` makes a permission denial, a status rule and an ordinary value
 * indistinguishable, so the sentence has to survive; it does not have to be
 * *printed beside the control*. On a dense grid it is routed to the cell's
 * `title`, which is the channel this app's grids already use.
 *
 * ⚠️ **A `title` on a DISABLED `Button` never renders** — `Button` carries
 * `disabled:pointer-events-none`, so it never receives the hover. Put the
 * sentence on a wrapper span.
 */
export type CreditNoteEditVerdict =
  | { editable: true }
  | { editable: false; reason: string };

/**
 * Whether the edit controls should be offered on this note.
 *
 * ## 🔴 TWO PARTS — owner decision 18 — AND BOTH ARE IMPLEMENTED
 *
 * The rule the owner took is: the note's **status carries `isEditable`**, AND
 * the note is **not already in Xero**. Both halves, or the control is
 * withheld.
 *
 * ```
 * status.isEditable   note.status.isEditable
 * not yet in Xero     note.inXero !== true
 * ```
 *
 * ✅ **The Xero half landed 2026-09-27.** The server serves `inXero` on every
 * credit-note read, computed from the same predicate its
 * `409 CREDIT_NOTE_IN_XERO` refuses on, so this function and the server's
 * refusal cannot disagree. ⚠️ **`=== true`, never truthiness on a default**: an
 * absent key is an older API that did not say, and it falls through to the
 * pre-existing behaviour with the 409 behind it.
 *
 * ⚠️ **It reads `inXero` and NOT `importedFromXero`, and NOT
 * `xeroCreditNoteNumber`.** The first says where a note came from, the second
 * is the number of the last push that succeeded — neither answers whether the
 * note is in Xero now. And NOT a `xero` link envelope: the credit-note read
 * carries none (see `CreditNote.inXero` for why), so the seam below named the
 * wrong artefact.
 *
 * ## ⛔ THE SEAM — ⚠️ *SUPERSEDED, the heading and every paragraph under it
 * down to "The half that IS implemented"; kept because the reasoning about
 * per-surface guards still binds*
 *
 * **The Xero half is not on the wire yet.** A credit note does not carry a
 * `xero` link envelope today, because the push is a later batch and is itself
 * gated on an unresolved scope probe. So this function currently offers the
 * edit controls on a note that may already exist in the accountant's Xero, and
 * **that is a known gap rather than the decided behaviour.**
 *
 * ⚠️ **Read the code below as HALF of owner decision 18.** The `tsc` build is
 * green, every call site compiles, and nothing anywhere will fail when the
 * envelope arrives — which is exactly why this paragraph exists rather than a
 * TODO somebody greps for. A guard that is merely incomplete looks identical
 * to a guard that is finished.
 *
 * ⛔ **THE OBSERVABLE EVENT THAT CLOSES IT: a `xero` link envelope appearing on
 * the credit-note read.** Not a migration, not a deploy, not a message saying
 * the push has shipped — the field coming back on a `GET`. When it does, the
 * second half is `linkOf(note).id !== null` withholding the controls, worded
 * as its own refusal, and this block goes with it.
 *
 * ⚠️ **Until then, do not let a call site "helpfully" add its own Xero test.**
 * A per-surface guard is how two screens come to disagree about whether one
 * note may be edited, and this module exists to stop that. The gap is recorded
 * here so it is closed in one place.
 *
 * ## The half that IS implemented
 *
 * ⚠️ **It reads the STATUS's flag and never the note's own state, and never a
 * status NAME.** `f_credit_note_statuses` is an admin-editable master, so a
 * name is a label somebody can rename with no migration and no notification —
 * this codebase has been bitten by exactly that four times (`"Unpaid"` read as
 * settled, `"Not Accepted"` painted as accepted, a product rename switching
 * campaign inclusions off, an inclusion renamed under a matcher). **Read the
 * flag; never `includes()` a name.**
 */
export function editableOf(note: CreditNote): CreditNoteEditVerdict {
  if (!note.status.isEditable) {
    /*
     * The status's own name is quoted back, because it is the only thing a
     * reader can act on — it tells them which status to move the note out of,
     * and an administrator may have renamed it since this build shipped.
     * ⚠️ The name is RENDERED here and matched on nowhere; those are different
     * things and only the second is forbidden.
     */
    return {
      editable: false,
      reason: `A credit note at ${note.status.name} cannot be edited.`,
    };
  }

  /*
   * THE SECOND HALF OF OWNER DECISION 18. Checked AFTER the status half, in the
   * same order the server's `assertEditable` checks them, so a note failing both
   * is given the status sentence here and `CREDIT_NOTE_NOT_EDITABLE` there.
   *
   * ⚠️ The sentence says where the change must happen instead — in Xero — and
   * why this screen refuses: the customer may already hold the document. It
   * does NOT offer "void it and raise another" as the only route, because an
   * amendment made in Xero itself is the ordinary one for a document already
   * there.
   */
  if (note.inXero === true) {
    return {
      editable: false,
      reason:
        'This credit note is already in Xero, so it cannot be edited here — the customer may ' +
        'already hold it. Make the change in Xero.',
    };
  }

  return { editable: true };
}

/* -------------------------------------------------------------------------
 * A company's credit balance — per currency, and never summed.
 * --------------------------------------------------------------------- */

/**
 * What a company's credit balance says in a cell.
 *
 * ```
 * []                            ->  '—'
 * [1,200.00 GBP]                ->  '1,200.00 GBP'
 * [1,200.00 GBP, 400.00 USD]    ->  '1,200.00 GBP, 400.00 USD'
 * ```
 *
 * **Not helper text** — a value. The *multiple-currency* wording it produces is
 * **EXCEPTION 4**: a fact about the data the reader cannot otherwise see.
 *
 * ## 🔴 IT IS AN ARRAY, ONE ROW PER CURRENCY, AND IT MUST NEVER BE SUMMED
 *
 * Owner decision 25: a credit balance is per company **per currency**, never
 * converted and never totalled across codes. ⛔ **A single figure is the bug,
 * not a simplification.** Adding 1,200.00 GBP to 400.00 USD produces 1,600 of
 * nothing — a number that is wrong by construction, renders perfectly, and is
 * the most confident thing on the panel.
 *
 * ⚠️ **AND A RATE TABLE EXISTING DOES NOT CHANGE THIS.** `f_exchange_rates`
 * exists and two target dashboards convert against it; **nothing here
 * converts, and nothing here may start.** A conversion needs a DATE and a
 * CHOSEN BASE, and a credit balance has neither: it is an aggregate over notes
 * raised across years, and nobody has been asked which currency they want it
 * in. The rate table removes the excuse, not the objection.
 *
 * ⛔ **THE FUNCTION'S SHAPE IS THE GUARD, AND IT IS DELIBERATE: IT RETURNS TEXT
 * AND NEVER A NUMBER, AND THIS MODULE EXPORTS NO `totalBalance`.** There is
 * nothing here to add up. Do not add one — an unused function returning a
 * plausible total is precisely what somebody wires up later without checking
 * what it is computed from.
 *
 * ⚠️ **Do not hand it an array filtered to one currency either.** That is a
 * sum performed by the caller, wearing this function's clothes: the cell then
 * reports one code's balance under a heading that names no code, and the
 * other currencies vanish with nothing on screen saying so. Pass what the
 * server sent.
 *
 * ## ⚠️ AN EMPTY ARRAY IS *no credit*, SO DO NOT HAND IT ONE YOU DO NOT HAVE
 *
 * `'—'` is a claim that this company holds nothing. A balance that failed to
 * load, or has not arrived, is a different fact and belongs in the caller's
 * own loading and error states. ⛔ **Never `?? []` at the boundary** — that
 * turns *we did not look* into *there is none*, on a figure somebody reads
 * before deciding whether to bill.
 *
 * ## The rendering
 *
 * ⚠️ **Every row carries its CODE, always, even when there is only one.** The
 * code is what makes a sum visibly refuse: a bare number is one somebody adds
 * to the number beside it. Same ruling as `compactMoneyParts` in
 * `src/lib/targets.ts`, which forbids a bare money figure for the same reason.
 *
 * ⚠️ **`formatDecimalString`, never `Number()`.** Money is an exact decimal
 * string and a float has no business anywhere near it.
 *
 * ⚠️ **Rows are rendered in the order the server sent them.** Sorting by
 * amount would put a company's largest balance first on one read and second on
 * the next as credit is spent, which reads as the row changing rather than the
 * money.
 */
/**
 * One pill item per currency, in the server's order — the pill form of
 * {@link balanceLabel} (owner's decision 2026-09-25). ⛔ Still never summed:
 * each pill carries its own code, which is what makes a sum visibly refuse.
 */
export function balancePills(
  balances: readonly CompanyCreditBalance[],
): { key: string; label: string }[] {
  return balances.map((b) => ({ key: b.currency, label: balanceRow(b) }));
}

export function balanceLabel(balances: readonly CompanyCreditBalance[]): string {
  // `orDash`'s glyph lives in `format.ts`; `formatDecimalString(null)` returns
  // the same em dash, so this file owns no second copy of that character.
  if (balances.length === 0) return formatDecimalString(null);
  return balances.map(balanceRow).join(', ');
}

/**
 * The whole balance for a cell's `title`, one currency per line — or
 * `undefined` when there is nothing to say.
 *
 * **EXCEPTION 4** — a fact about the data. It is the recovery path for text the
 * cell has already cut off, which is the established pattern here
 * (`companyProjectsTitle`, and the line grids' truncated Item cell).
 *
 * 🔴 **IT IS LOAD-BEARING ON ANY COMPANY TRADING IN MORE THAN ONE CURRENCY, AND
 * THAT IS THE CASE WHERE BEING WRONG COSTS MOST.** {@link balanceLabel}
 * truncates by CSS in a dense cell, and what an ellipsis eats is the TAIL —
 * the second and third currencies. So a reader sees `1,200.00 GBP…` and takes
 * it for the whole balance, which is the single-figure reading owner decision
 * 25 exists to prevent, arriving through a stylesheet instead of through
 * arithmetic. **A caller rendering the label without this `title` has built a
 * cell that silently omits money.**
 *
 * ⚠️ **One per line rather than a comma join**, deliberately unlike
 * `companyProjectsTitle`: these are figures, and figures stacked in a column
 * are read as separate amounts where a comma-joined run reads as one sentence
 * somebody might add up.
 *
 * ⚠️ **`undefined` rather than `''`.** React omits the attribute entirely for
 * `undefined`, where `title=""` is a real empty tooltip on some browsers — and
 * the cell already says `'—'`, which needs no elaboration.
 */
export function balanceTitle(
  balances: readonly CompanyCreditBalance[],
): string | undefined {
  if (balances.length === 0) return undefined;
  return balances.map(balanceRow).join('\n');
}

/**
 * One currency's balance, spelled the one way.
 *
 * ⚠️ **THE FIELD IS `balance`, NOT `amount`, AND THE DIFFERENCE IS NOT
 * COSMETIC.** `CompanyCreditBalance` carries THREE figures — `creditRaised`,
 * `creditAllocated` and `balance` — and only the third is what is left to
 * spend. Reading `creditRaised` here would print the whole of a company's
 * credit history as though none of it had been used, on a cell whose entire
 * job is to say how much is available.
 */
function balanceRow(balance: CompanyCreditBalance): string {
  return `${formatDecimalString(balance.balance)} ${balance.currency}`;
}

/* -------------------------------------------------------------------------
 * A company's balance on a LIST row — `/companies` and the UP dashboard's
 * Companies card, owner's decision 2026-09-24.
 * --------------------------------------------------------------------- */

/**
 * The `title` on a cell whose balance is not on the row — the reader holds no
 * `credit-notes.read` grant, or the server predates the embed. ⚠️ Worded so it
 * claims NOTHING about the company's credit: an unread balance is not "none".
 */
export const CREDIT_BALANCE_NOT_VISIBLE = 'Credit balance not available to show here';

/**
 * What a list cell may say about a company's credit, from the row's embed.
 *
 * Three facts, and folding any two is the defect:
 *
 * - **`unknown`** — `null` (no grant) or `undefined` (an older server that
 *   sends no key). ⛔ Never rendered as a figure, and never as *no credit*.
 * - **`none`** — `[]`, or every entry at zero. A zero-balance entry is a
 *   currency the company once held credit in and has spent; it is not credit
 *   HELD, so it does not reach the cell.
 * - **`held`** — the entries with a non-zero balance, in the order the server
 *   sent them, for {@link balanceLabel} / {@link balanceTitle}.
 *
 * ⚠️ **Dropping the zero entries is NOT the one-currency filter
 * {@link balanceLabel} forbids** — that warning is about a caller choosing a
 * code and hiding the rest; this drops only rows whose balance is nothing, and
 * every currency that holds credit still renders.
 *
 * ⚠️ **A non-numeric string is kept, not dropped**: the zero test is an exact
 * decimal-string match, so anything it cannot read is shown rather than hidden.
 */
export type CreditHeld =
  | { kind: 'unknown' }
  | { kind: 'none' }
  | { kind: 'held'; balances: readonly CompanyCreditBalance[] };

export function creditHeldOf(
  balances: readonly CompanyCreditBalance[] | null | undefined,
): CreditHeld {
  // `undefined` folds to the same fact as `null` HERE, once — never `?? []`.
  if (balances === null || balances === undefined) return { kind: 'unknown' };
  const held = balances.filter((entry) => !isZeroDecimal(entry.balance));
  return held.length === 0 ? { kind: 'none' } : { kind: 'held', balances: held };
}

function isZeroDecimal(value: string): boolean {
  return /^[-+]?0*(\.0*)?$/.test(value.trim()) && /\d/.test(value);
}

/* -------------------------------------------------------------------------
 * Naming a note in a sentence.
 * --------------------------------------------------------------------- */

/**
 * How a credit note is NAMED in a sentence a person reads.
 *
 * 🔴 **THERE IS NO `creditNoteNumber`, AND THE OBVIOUS SUBSTITUTE IS THE TRAP.**
 * A quote has `quoteNumber` — the CRM's own reference, allocated from a ledger
 * — and a credit note has no counterpart. The only number it can carry is
 * `xeroCreditNoteNumber`, which is **Xero's**, is written by the push and by
 * nothing else, and is therefore **`null` on every credit note that exists
 * today**, because the push is not built.
 *
 * ⛔ So interpolating that field directly puts the word `null` into a
 * confirmation dialog about destroying money. It would not throw, it would not
 * fail a build, and it would read as a bug in the app at the moment somebody is
 * deciding whether to void a document.
 *
 * ⚠️ **The fallback is the SUBJECT, not the id.** `subject` is `NOT NULL` on
 * the column, so there is always one, and it is what the person typed and what
 * every list shows. An id would be correct and unrecognisable — a reader
 * confirming a destructive action needs to know WHICH note, and `#417` answers
 * that only for somebody already looking at the URL.
 *
 * ⚠️ **It does NOT assert the absence of a number.** If the push ever lands and
 * starts filling that column, this function begins printing the Xero reference
 * with no edit — which is the point of putting the choice here rather than at
 * three call sites that would each have to be found again.
 */
export function creditNoteRef(note: CreditNote): string {
  return note.xeroCreditNoteNumber ?? note.subject;
}

/* -------------------------------------------------------------------------
 * The destructive controls.
 * --------------------------------------------------------------------- */

/**
 * What voiding this note actually does — for the `ConfirmDialog` body.
 *
 * **EXCEPTION 3** — a destructive consequence. `ConfirmDialog` body text naming
 * what is about to be destroyed is the structural exception the house rule
 * already carves out, and this app's confirmations are required to name the
 * record and state the real consequence.
 *
 * ## ⛔ NEVER PROMISE AN UNDO, IN ANY FORM
 *
 * Un-applying credit **DELETES the allocation row**. There is no contra row,
 * no reversal entry and no history of the allocation having existed — so
 * wording this as *"you can re-apply it later"* is a promise about a record
 * that will not be there. **Say what happens; do not reassure.**
 *
 * ⚠️ **The two branches send a reader to opposite places and must stay
 * separate.** Voiding a note that carries allocations is **refused
 * server-side, with a `409`** — so the dialog's job on that branch is to say
 * what to un-apply first, not to warn about a consequence that will never
 * happen. Collapsing the two into one sentence either threatens a destruction
 * the server will refuse, or hides the refusal behind a warning.
 *
 * ⚠️ **Nothing here asserts that the other branch will succeed.** The
 * allocation count is what this app can see; the server may refuse for a
 * reason it has not been told about, and the refusal is the guarantee while
 * the dialog is only the optimisation.
 */
export function voidWarning(
  note: CreditNote,
  allocations: readonly CreditNoteAllocation[],
): string {
  if (allocations.length > 0) {
    /*
     * EXCEPTION 1 on this branch — a refusal, stated before it is collected
     * rather than after. It names the count because "un-apply the allocations"
     * without a number cannot be acted on: a reader has no way to know whether
     * they are looking for one or nine.
     */
    const noun = allocations.length === 1 ? 'allocation' : 'allocations';
    const it = allocations.length === 1 ? 'it' : 'them';
    return (
      `This credit note has ${allocations.length} ${noun} against invoices, and voiding it will be refused ` +
      `while ${it} stand. Un-apply ${it} first — un-applying credit deletes the allocation, which is not recorded anywhere afterwards.`
    );
  }

  return (
    `Voiding ${creditNoteRef(note)} withdraws its credit from ${CREDIT_BALANCE_LABEL.toLowerCase()} on this company. ` +
    `The note stays on the account as a voided record.`
  );
}

/**
 * Why deleting this note will be refused, or `null` when nothing this app can
 * see blocks it.
 *
 * **EXCEPTION 1** — a refusal.
 *
 * ⛔ **`null` IS NOT A PROMISE THAT THE DELETE WILL SUCCEED, and a caller must
 * never word it as one.** It means *this app has no reason to refuse it*. The
 * server holds reasons this app cannot see — the allocation foreign key is
 * `RESTRICT`, and it counts rows rather than trusting a caller — so the
 * refusal must still be handled where it arrives. **The disabled control is
 * the optimisation; the `409` is the guarantee.**
 *
 * ⚠️ **It reads the ALLOCATIONS, not `isVoid` and not `spendable`.** A voided
 * note with allocations is still un-deletable, and a draft with none is
 * deletable — the void state and the delete rule are independent, and reading
 * one for the other is a confident wrong answer in both directions.
 *
 * ⚠️ **It names what to do rather than asserting what cannot be done.** *"This
 * cannot be deleted"* is a dead end and a claim about the whole system; naming
 * the allocations is a positive statement that fails visibly if the rule ever
 * moves.
 */
export function deleteRefusal(
  note: CreditNote,
  allocations: readonly CreditNoteAllocation[],
): string | null {
  if (allocations.length === 0) return null;

  const noun = allocations.length === 1 ? 'allocation' : 'allocations';
  const it = allocations.length === 1 ? 'it' : 'them';
  return (
    `${creditNoteRef(note)} has ${allocations.length} ${noun} against invoices and cannot be deleted while ${it} stand. ` +
    `Un-apply ${it} first, or void the note to withdraw its credit and keep the record.`
  );
}

/* -------------------------------------------------------------------------
 * Approval.
 * --------------------------------------------------------------------- */

/**
 * What approving this note does — one sentence, for the confirmation.
 *
 * **EXCEPTION 3** — a consequence. It is not destructive, and it is on that
 * list for the same reason a destructive one is: **it changes what the
 * business can do with real money, and nothing else on the screen says so.**
 *
 * ## Why it needs a sentence at all
 *
 * Approving is what makes a note's value **spendable against the company's
 * invoices** — that is the whole reason `approve` is a separate permission
 * that Accounts does not hold. A reader pressing it from a document screen has
 * no way to see that consequence: the note looks the same afterwards, the
 * figure on it does not move, and what changes is a balance on a different
 * panel.
 *
 * ⚠️ **It names the FIGURE, and `remainingAmount` is the right field to name —
 * on this branch specifically.** Rule 2 in the header says `remainingAmount`
 * must never be worded as *credit available*, and that rule is not being bent
 * here: a note awaiting approval has spent nothing, so its remaining value IS
 * exactly what approval makes spendable. **That equivalence holds because the
 * note is unapproved, not in general** — which is why the sentence lives on
 * this function and not on the label.
 *
 * ⚠️ **It says *spendable*, never *approved*, about the outcome.** Rule 1: an
 * approved note that is later voided is still approved and is not spendable,
 * so *approved* is the state and *spendable* is the consequence. A sentence
 * promising the second by naming the first is the reassuring direction.
 */
export function approvalConsequence(note: CreditNote): string {
  return (
    `Approving ${creditNoteRef(note)} makes ${formatDecimalString(note.remainingAmount)} ${note.currency} ` +
    `spendable against this company's invoices.`
  );
}

/**
 * EXCEPTION 3 — a consequence sentence (owner 2026-10-28). Approving or
 * submitting a credit note never emails the customer: approve authorises the
 * note in Xero, submit only moves it to Awaiting approval. An Accounts user
 * asked to approve "without sending it to the customer", so the confirm dialogs
 * say so. One copy; import it, never re-inline it.
 */
export const CREDIT_NOTE_NOT_SENT_NOTE = 'Nothing is sent to the customer.';

/* -------------------------------------------------------------------------
 * RAISING A CREDIT NOTE FROM AN INVOICE
 *
 * ✅ ONE COPY, BOTH SURFACES — `InvoicesPage` and `InvoiceDetailPage` import
 * this and neither declares its own. It lived in both page files until
 * 2026-09-15 and each carried a note saying so; what would have drifted is
 * **which flag decides** and **what a reader is told when it cannot be read**,
 * on a control that hands money back.
 *
 * ⚠️ **THE FETCH DELIBERATELY DID NOT COME WITH IT.** `usePaidStatusFlags` is
 * still declared in each page, because this module may not fetch (see the
 * header) — and that is the cheap half to duplicate: a hook answering *"has the
 * status list arrived"* has no wording and no rule in it, where the gate below
 * has both.
 *
 * ## ✅ THE OWNER'S RULE IS ENFORCED — `isAwaitingPayment || isPaid`, 2026-09-15
 *
 * *SUPERSEDED, the rule this was first built to: "a credit note can be raised
 * only if the invoice is PAID."*
 *
 * **The rule is: any invoice that has been ISSUED — anything except a Draft.**
 * The owner's reason is a tax point, not a payment: a credit note is also raised
 * when an invoice is **awaiting payment** and the customer cancels, **because
 * the tax has already been paid on the invoice** and it therefore cannot simply
 * be voided. So paid, awaiting payment and overdue all qualify; a Draft does
 * not, and is edited or deleted instead.
 *
 * 🔴 **AND THE SECOND RULING, SAME DAY: A CANCELLED INVOICE IS NOT CREDITABLE.**
 * *SUPERSEDED, the sentence this function used to render: "whether one of those
 * may be credited has not been decided."* The demand was withdrawn, so there is
 * nothing to give back. ⛔ **It is a DECISION, not a limitation of EMERGE**, and
 * the refusal must not be worded as one.
 *
 * 🔴 **AND A THIRD, 2026-09-24: A CREDITED INVOICE IS NOT CREDITABLE EITHER**,
 * and the SERVER now refuses all three (Draft, Cancelled, Credited) at copy
 * time with `CREDIT_NOTE_SOURCE_NOT_CREDITABLE`, using this same expression.
 *
 * ⚠️ **ONE EXPRESSION CARRIES BOTH RULINGS, which is why it is not two tests.**
 * Across the seeded rows:
 *
 * ```
 *                        isSettled  isPaid  isAwaitingPayment   credited?
 *   Draft                    0        0            0             refused
 *   Awaiting Payment         0        0            1             ALLOWED
 *   Paid                     1        1            0             ALLOWED
 *   Cancelled                1        0            0             refused
 * ```
 *
 * `isAwaitingPayment || isPaid` admits exactly the middle two and refuses the
 * outer two. **Draft and Cancelled fall out of the same positive test**, so
 * there is no second rule to keep in step with this one.
 *
 * ⛔ **NEVER A NEGATIVE `!isDraft` TEST.** There is no `is_draft` column, so the
 * only spelling would be *what it is not* — which sweeps in **every future
 * status nobody has classified**: an administrator adds "On hold" tomorrow, sets
 * no flags because none obviously applies, and it starts qualifying, handing
 * money back with a `201` and nothing failing. The backend has refused that
 * shape twice (`commission-runs.repository.ts`'s `draftPredicate`, and migration
 * 202). A positive test excludes an unclassified status until somebody
 * classifies it, which is the direction that cannot hand money back.
 *
 * ⛔ **AND `isSettled` IS NO PART OF IT — it is TRUE ON CANCELLED.** Gating on it
 * offers to credit work nobody was ever billed for. It appears below only to
 * WORD a refusal, never to grant one.
 *
 * ⛔ **AND NEVER BY NAME.** `"Unpaid"` contains `"paid"`. This codebase has been
 * bitten four times by a substring test on an admin-editable master.
 *
 * ## 🔴 THE FLAG COMES FROM `/invoice-statuses`, NOT FROM THE TRACKER'S LABELS
 *
 * *SUPERSEDED, and it was true when written: "`is_awaiting_payment` reaches no
 * client at all."* It reaches **one**. The registry's `booleanColumns` for
 * `f_invoice_statuses` now carries four entries — `is_settled`, `is_paid`,
 * `is_credited`, `is_awaiting_payment` — so this gate reads {@link InvoiceStatus}
 * from `/invoice-statuses`.
 *
 * ⛔ **`/invoice-tracker/status-labels` STILL SERVES ONLY `{statusId, name,
 * label, isSettled, isPaid}`, and this gate used to read THAT.** Measured in
 * `invoice-tracker.repository.ts`'s `listStatusLabels`, whose `SELECT` names
 * `st.is_settled` and `st.is_paid` and no third flag. **Pointing this gate back
 * at it would not fail** — `isAwaitingPayment` would read `undefined` on every
 * row, the expression would collapse to exactly `isPaid`, and the too-narrow
 * rule would return underneath a function whose doc says it enforces the new
 * one. That is the `leadSourceText` shape: a key a client declares and a server
 * never sends renders falsy for ever, with nothing to find.
 *
 * ⚠️ **`InvoiceTrackerStatusLabel` is untouched and still right for its own
 * screen** — `StatusLabelMapper` maps labels and needs no third flag. The two
 * types are not interchangeable and neither is a widening of the other.
 *
 * ⚠️ **Do not let a call site add its own status test.** A per-surface guard is
 * how two screens come to disagree about whether one invoice may be credited,
 * and this module exists to stop that.
 * --------------------------------------------------------------------- */

/**
 * Whether this invoice may have a credit note raised from it, and if not, the
 * sentence that says why.
 *
 * `reason` is **EXCEPTION 2** — a why-this-is-disabled sentence. A bare
 * `disabled` makes a permission denial, an unissued invoice and a failed fetch
 * identical, so the sentence travels with the verdict rather than being
 * inferred at the call site.
 *
 * ⚠️ **A `title` on a DISABLED `Button` never renders** — `Button` carries
 * `disabled:pointer-events-none`, so it never receives the hover. Put the
 * sentence on a wrapper span.
 */
export type CreditNoteGate = { allowed: true } | { allowed: false; reason: string };

export function creditNoteGateOf(
  invoice: Invoice,
  /** `ability.canCreate('credit-notes')` — see the permission note below. */
  canCreateCreditNote: boolean,
  /** `denialReason(...)`, which is `null` even on a refusal the ability cannot explain. */
  denial: string | null,
  /**
   * The `/invoice-statuses` master. `null` while loading, or when the read
   * failed — the two are told apart by `failed`.
   *
   * ⛔ **NOT `InvoiceTrackerStatusLabel`.** That list carries no
   * `isAwaitingPayment`, so it cannot answer the question below and would
   * answer it falsely-negatively instead. See the block above.
   */
  statuses: readonly InvoiceStatus[] | null,
  failed: boolean,
): CreditNoteGate {
  /*
   * 🔴 **THE PERMISSION REFUSAL OUTRANKS EVERY OTHER BRANCH, and the order is
   * the point rather than an optimisation.** A reader who may not create credit
   * notes at all is told THAT, once, in those words — rather than meeting a
   * sentence about an invoice status on row after row, which is a true
   * statement about something they could not act on even if it changed. The
   * same ruling the UP dashboard's `cellGate` records.
   *
   * ⚠️ **`credit-notes` carries NO `requireRole` — the permission SET decides,
   * with a scope that genuinely narrows** (the `tasks`/`notes`/`targets` class;
   * migration 200 gives `accounts` read, create and update while withholding
   * delete and approve). So `canCreate` here is a real answer rather than a role
   * guess, and it is still only the optimisation: the server's refusal is the
   * guarantee, because a grant can change under an access token that lasts days.
   *
   * ⚠️ **`denialReason` returns `null` for a refusal the ability cannot
   * explain**, so the fallback sentence is not decoration — without it a refused
   * reader gets a disabled control with nothing on it.
   */
  if (!canCreateCreditNote) {
    return {
      allowed: false,
      reason: denial ?? 'Your account cannot raise credit notes. Ask an administrator.',
    };
  }

  /*
   * The two structural blockers first, because they are properties of the
   * invoice and are true whatever the status says. `companyId` and `currency`
   * are both `NOT NULL` on `d_credit_notes` and neither is settable afterwards
   * — a credit BALANCE is struck at `(companyId, currency)` — so an invoice
   * missing either cannot seed a note at all. Saying so beats opening a form
   * that cannot be completed from this invoice.
   */
  if (!invoice.company) {
    return { allowed: false, reason: 'This invoice is not on a company, so there is no account to credit.' };
  }
  if (!invoice.currency) {
    return { allowed: false, reason: 'This invoice records no currency, so there is no currency to credit in.' };
  }

  /*
   * ⚠️ **THE UNKNOWN BRANCHES ARE WORDED WITHOUT NAMING A RULE, AND THAT IS
   * DELIBERATE RATHER THAN VAGUE.** Each says *whether this may be credited is
   * unknown* rather than naming whichever test happens to be in force below, so
   * they stayed true through the move from the paid rule to the issued one. ✅
   * **That earned its keep: the rule changed and not one of these sentences had
   * to.** A refusal that names the rule it was written beside is a refusal that
   * has to be found again; these do not.
   *
   * ⚠️ **A FAILED READ IS UNKNOWN — never "not creditable" and never
   * "creditable".** A request that did not come back is not evidence about an
   * invoice.
   */
  if (failed) {
    return {
      allowed: false,
      reason: 'The invoice statuses couldn’t be read, so whether this invoice may be credited is unknown.',
    };
  }
  if (statuses === null) {
    return { allowed: false, reason: 'Checking this invoice’s status.' };
  }
  if (!invoice.status) {
    return { allowed: false, reason: 'This invoice has no status, so whether it may be credited is unknown.' };
  }

  /*
   * ⚠️ **`id`, not `statusId`** — this is the master row itself now, not the
   * tracker's label projection over it. A document's own `invoice.status` is a
   * bare `MasterRef` carrying no flags, so resolving it against the master is
   * the only way to ask what the status MEANS.
   */
  const row = statuses.find((candidate) => candidate.id === invoice.status?.id);
  /*
   * A status the list does not carry is a status this client cannot classify —
   * an id from a server newer than the list in hand, or a master that moved
   * under an open page. Not a licence to fall back to its NAME.
   */
  if (!row) {
    return {
      allowed: false,
      reason: 'This invoice’s status isn’t one we can classify, so whether it may be credited is unknown.',
    };
  }

  /*
   * 🔴 **THE RULE. The POSITIVE test, and it carries BOTH of the owner's
   * rulings** — issued invoices qualify, and a cancelled one does not. See the
   * truth table in the block above; nothing else here decides.
   */
  if (row.isAwaitingPayment === true || row.isPaid === true) return { allowed: true };

  /*
   * Everything below only WORDS the refusal. Ordered most-specific first,
   * because each test would otherwise be true of the row beneath it.
   */

  /*
   * ⚠️ **CREDITED IS CHECKED BEFORE CANCELLED, and it has to be.** The
   * `Credited` row lands on `isSettled 1, isPaid 0, isAwaitingPayment 0` — the
   * triple `Cancelled` already carries — so the cancelled test below is true of
   * it too and would tell a reader their credited invoice was cancelled.
   *
   * ⚠️ **`=== true`, so an older server without the flag falls through to the
   * cancelled sentence** — which is exactly what such a row said before this
   * flag existed. Degrading to the previous wording is the safe direction.
   *
   * 🔴 **CREDITED — A DECISION NOW, owner's ruling 2026-09-24.** *SUPERSEDED:
   * "This invoice is already recorded as credited. Whether a further credit
   * note may be raised from it has not been decided."* It may not: the server
   * refuses at copy time with `CREDIT_NOTE_SOURCE_NOT_CREDITABLE`, so the
   * sentence states the rule rather than an open question. ⛔ As with
   * Cancelled below, do not reword it as something EMERGE cannot do.
   */
  if (row.isCredited === true) {
    return {
      allowed: false,
      reason: 'This invoice has already been credited, so there is nothing left to credit.',
    };
  }

  /*
   * 🔴 **CANCELLED — A DECISION, NOT A LIMITATION.** *SUPERSEDED: "Whether one
   * of those may be credited has not been decided."* The owner ruled on
   * 2026-09-15 that it may not, so the sentence says so plainly and gives the
   * reason in the reader's own terms — **the demand was withdrawn, so there is
   * nothing to give back.** ⛔ Do not reword it as something EMERGE cannot do:
   * that invites somebody to "fix" it.
   *
   * ⚠️ **The settled-and-unpaid derivation decides WORDING and nothing else**,
   * which is why it is acceptable here where migration 158 rejected the
   * identical pair for choosing a destructive Xero action. Worst case a
   * "Written Off" status somebody adds later reads this sentence — on a control
   * that refuses either way, by the positive test above.
   */
  if (row.isSettled === true) {
    return {
      allowed: false,
      reason:
        'This invoice is cancelled, so it cannot be credited — the demand for payment was withdrawn, ' +
        'and there is nothing to give back.',
    };
  }

  /*
   * ⚠️ **UNKNOWN, NOT "DRAFT", WHEN THE FLAG NEVER ARRIVED.** Against a server
   * predating the registry entry, `isAwaitingPayment` is absent on every row —
   * so Draft and Awaiting Payment are once again byte-identical here, and the
   * draft sentence below would be a flat falsehood on the commonest invoice
   * there is, sending its reader to look for a draft that does not exist.
   *
   * ⚠️ **`typeof`, not truthiness**: `false` is a real answer (it is what Draft
   * carries) and must reach the draft branch rather than this one.
   */
  if (typeof row.isAwaitingPayment !== 'boolean') {
    return {
      allowed: false,
      reason:
        'EMERGE can’t tell whether this invoice has been issued yet, so whether it may be credited ' +
        'is unknown.',
    };
  }

  /*
   * A draft. The last row standing, and the only one the owner's rule names
   * directly — so this is the one sentence here allowed to state the rule.
   */
  return {
    allowed: false,
    reason:
      'This invoice is still a draft, so it hasn’t been issued and there is nothing to credit. ' +
      'Edit or delete it instead.',
  };
}

/* -------------------------------------------------------------------------
 * Xero — what a push did, worded once (2026-10-03).
 *
 * Owner decisions, closed: approving here approves the note in Xero too;
 * applying credit here is pushed to Xero after it is saved, and a refused or
 * unreachable push KEEPS the allocation (shown as not in Xero, with a retry);
 * un-applying an allocation that is in Xero removes it there first; voiding a
 * note that is approved in Xero voids it there too.
 *
 * Every sentence below is **EXCEPTION 4 — a fact about the data the reader
 * cannot otherwise see** (what the other system now holds), or EXCEPTION 1 (a
 * refusal), or EXCEPTION 3 (a destructive consequence). Nothing else.
 *
 * ⛔ **`skipped` IS NEVER WORDED OR TONED AS AN ERROR.** It is the ordinary
 * answer when the note or the invoice is not in Xero yet, or the invoice is
 * still a draft there — the server's own `reason` says which, and it is shown
 * verbatim. A red line on the ordinary case teaches people to ignore the one
 * that matters.
 * --------------------------------------------------------------------- */

/**
 * One line reporting a push. `success` is a quiet confirmation, `neutral` an
 * ordinary not-sent, `danger` a refusal. The caller picks the colour from the
 * tone; the text is complete on its own.
 */
export interface XeroOutcomeLine {
  tone: 'success' | 'neutral' | 'danger';
  text: string;
}

/** The Xero column's two states. Labels, not helper text. */
export const ALLOCATION_IN_XERO_LABEL = 'In Xero';
export const ALLOCATION_NOT_IN_XERO_LABEL = 'Not in Xero';
/** The retry control's label. */
export const SEND_ALLOCATION_TO_XERO_LABEL = 'Send to Xero';

/**
 * Whether Xero holds this allocation. Reads the server's `inXero`, and only
 * against an older server that did not send it falls back to the timestamp —
 * never to `false`, which would offer a push of something already in Xero.
 */
export function allocationInXero(allocation: CreditNoteAllocation): boolean {
  return allocation.inXero ?? allocation.xeroAllocatedAt !== null;
}

/**
 * What applying credit, or retrying the push, did in Xero.
 *
 * ⚠️ On a `failed` or `skipped` APPLY the sentence says the credit is applied
 * here anyway — the allocation is kept and the balance counts it, and a reader
 * told only that Xero refused would apply it a second time.
 */
export function allocationPushOutcome(
  push: AllocationPush,
  occasion: 'apply' | 'retry',
): XeroOutcomeLine {
  const here = occasion === 'apply' ? 'The credit is applied in EMERGE. ' : '';
  switch (push.status) {
    case 'pushed':
      return {
        tone: 'success',
        text: occasion === 'apply' ? 'Applied, and sent to Xero.' : 'Sent to Xero.',
      };
    case 'skipped':
      return { tone: 'neutral', text: `${here}Not sent to Xero: ${push.reason}` };
    case 'failed':
      return { tone: 'danger', text: `${here}Xero did not accept it: ${push.message}` };
  }
}

/**
 * What approving or voiding did to the note in Xero.
 *
 * ⚠️ The EMERGE half stands whatever this says — the approval or the void has
 * already happened here — so a `failed` line leads with that, and never reads
 * as the action having failed.
 */
export function creditNotePushOutcome(
  push: CreditNotePushReport,
  action: 'approve' | 'void',
): XeroOutcomeLine {
  const done = action === 'approve' ? 'Approved' : 'Voided';
  switch (push.status) {
    case 'pushed':
      return { tone: 'success', text: `${done} in Xero as well.` };
    case 'skipped':
      return { tone: 'neutral', text: `${done} in EMERGE. Xero was not changed: ${push.reason}` };
    case 'failed':
      return {
        tone: 'danger',
        text: `${done} in EMERGE, but Xero did not accept it: ${push.message}`,
      };
  }
}

/**
 * The allocation pushes `approve` reports, as as few lines as say everything.
 *
 * ⚠️ **Grouped by outcome and sentence**, because the commonest non-pushed case
 * is every allocation skipped for ONE reason (the note itself did not reach
 * Xero), and repeating that reason per row buries it. A group of one names its
 * invoice; `nameOf` returns `null` where the allocation could not be resolved.
 */
export function allocationPushesOutcome(
  pushes: readonly { allocationId: number; xeroPush: AllocationPush }[],
  nameOf: (allocationId: number) => string | null,
): XeroOutcomeLine[] {
  if (pushes.length === 0) return [];

  const pushed = pushes.filter((entry) => entry.xeroPush.status === 'pushed').length;
  const lines: XeroOutcomeLine[] = [];
  if (pushed > 0) {
    lines.push({
      tone: 'success',
      text:
        pushed === 1
          ? '1 credit already applied here was sent to Xero.'
          : `${pushed} credits already applied here were sent to Xero.`,
    });
  }

  const groups = new Map<string, { tone: 'neutral' | 'danger'; sentence: string; ids: number[] }>();
  for (const { allocationId, xeroPush } of pushes) {
    if (xeroPush.status === 'pushed') continue;
    const tone = xeroPush.status === 'skipped' ? 'neutral' : 'danger';
    const sentence = xeroPush.status === 'skipped' ? xeroPush.reason : xeroPush.message;
    const key = `${tone}\u0000${sentence}`;
    const group = groups.get(key) ?? { tone, sentence, ids: [] };
    group.ids.push(allocationId);
    groups.set(key, group);
  }

  for (const { tone, sentence, ids } of groups.values()) {
    const name = ids.length === 1 ? nameOf(ids[0]!) : null;
    const subject =
      ids.length === 1
        ? `The credit applied to ${name ?? 'one invoice'} was`
        : `${ids.length} credits already applied here were`;
    lines.push({
      tone,
      text:
        tone === 'neutral'
          ? `${subject} not sent to Xero: ${sentence}`
          : `${subject} not accepted by Xero: ${sentence}`,
    });
  }
  return lines;
}

/**
 * 🔴 **EXCEPTION 3 — un-applying an allocation that is in Xero removes it THERE
 * too**, first; if Xero refuses, nothing is removed in either system. Shown in
 * the un-apply dialog only when the allocation is in Xero.
 */
export const UNAPPLY_REMOVES_FROM_XERO =
  'It is also removed from Xero, first. If Xero refuses, nothing is removed here either.';

/** The refusal code the server answers when Xero would not remove an allocation. */
export const XERO_ALLOCATION_REMOVE_FAILED_CODE = 'XERO_ALLOCATION_REMOVE_FAILED';
